import React, { Suspense, useEffect, useState, useRef } from 'react';
import templateData from '../../utility/getTemplateData';
import { getPPCCStatus } from '../../utility/helper';
import PPCC from '../../components/ppcc';
import {
  Tile,
  Col,
  Button,
  Sheet,
  LoadingSpinner,
  BodyText,
  HeadingText,
} from '@paypalcorp/pp-react';
import classNames from 'classnames';

import {
  GoalProgressMeter,
  useHasScrolledToPosition,
  CampaignImage,
  Title,
  AdvancedEndDateBadge,
} from '@paypalcorp/donate-react';
import { hideSpinnerOverlay } from '../../utility/helper';
import Landing from './landing';
import withTheme from '../../components/withTheme';
import {
  roundUpAndFormatCurrency,
  parseDateOnly,
} from '../../utility/formator';
import { lazy as reactLazy } from 'react';
import { addClass } from '../../utility/domUtils';
import getContent from 'pp-react-l10n';
import WhosDonatedBanner from '../../components/WhosDonatedBanner';
import Flyout from '../../components/Flyout';
import { fetcher } from '../../utility/domUtils';
import { formatFeatured } from '../../utility/donorWall';
import ConfirmationSocialShare from '../../confirmation/ConfirmationSocialShare';
import { localsMessages } from '../../utility/getTemplateData';
import { MAX_MOBILE_WIDTH } from '../../setup/clientConstants';
import throttle from 'lodash/throttle';
import { trackDonateLandingButtonClick } from '../../fpti/fpti';

const LandingDonorWall = reactLazy(() =>
  import(
    /* webpackChunkName: "LandingDonorWall" */ '../../components/LandingDonorWall'
  )
);

const LandingRichTextDisplay = reactLazy(() =>
  import(
    /* webpackChunkName: "LandingRichTextDisplay" */ '../../components/LandingRichTextDisplay'
  )
);

const getCampaignContent = getContent('locals')('landing')('campaigns');
const getSocialContent = getContent('locals')('landing')('social');
let campaignsText = getContent('locals')('common/campaigns');

const messages = localsMessages('landing') || {};

const MAX_HEIGHT_BANNER = 380;
const MAX_HEIGHT_DESCRIPTION = 800;

const MERCHANT_PPCC_CONFIRMED_STATUS = 'CONFIRMED';

function isCampaignEnded(endDate) {
  return (
    endDate && new Date().setHours(0, 0, 0, 0) > endDate?.setHours(0, 0, 0, 0)
  );
}

const CampaignLanding = () => {
  const [isPaymentCardOpen, setIsPaymentCardOpen] = useState(false);
  const amountRaised = templateData.campaignTotalAmountRaised || '0';
  const campaignStatus = templateData.campaignStatus;
  const targetAmount = templateData.campaignTargetAmount;
  const campaignEndTime = templateData.campaignEndTime;
  const campaignTotalNumberOfTxn = templateData.campaignTotalNumberOfTxn;
  const amountRaisedWithCurrencySymbol = roundUpAndFormatCurrency(
    amountRaised,
    templateData.donationCode
  );
  const donationName = templateData.donationName ?? '';
  const [contributionsList, setContributionsList] = useState({
    featured: [],
    list: [],
  });
  const [contributionsError, setContributionsError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [enableReadMore, setEnableReadMore] = useState(true);
  const [descriptionHeight, setDescriptionHeight] = useState(
    MAX_HEIGHT_DESCRIPTION
  );
  const [originalDescriptionHeight, setOriginalDescriptionHeight] = useState(0);

  const stickToTop = useHasScrolledToPosition(650);

  const [isMobile, setIsMobile] = useState(false);

  const checkDeviceType = () => {
    setIsMobile(window.innerWidth < MAX_MOBILE_WIDTH, 200);
  };

  useEffect(() => {
    checkDeviceType();
    window.addEventListener('resize', throttle(checkDeviceType, 200));
    return () => {
      window.removeEventListener('resize', throttle(checkDeviceType, 200));
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const result = await fetcher(
        `/donate/api/donorwall/${templateData.productId}/${currentPage}/10`
      ).then((response) => response.json());
      if (result.contributions) {
        setContributionsList({
          featured:
            contributionsList.featured.length !== 0
              ? contributionsList.featured
              : formatFeatured(
                  result?.contributions?.contributions?.featured,
                  campaignTotalNumberOfTxn
                ),
          list: [
            ...contributionsList.list,
            ...(result?.contributions?.contributions?.list ?? []),
          ],
        });
      } else {
        setContributionsError(true);
      }
      setIsLoading(false);
    };

    setIsLoading(true);
    setContributionsError(false);
    fetchData();
  }, [currentPage, retry]);

  const handleShowMore = () => {
    setCurrentPage(currentPage + 1);
  };

  useEffect(() => {
    addClass('#mainWrapper header.donate_global_nav', ['hide-header']);
    addClass('#donateWrapper', ['is-landing']);
    hideSpinnerOverlay();
  }, []);

  const goalAmtWithCurrencySymbol = roundUpAndFormatCurrency(
    targetAmount,
    templateData.donationCode
  );

  let campaignGoalTitle = campaignsText('campaignGoal.title', {
    goalAmount: goalAmtWithCurrencySymbol,
  });

  const renderEndDateBadge = () => (
    <div className="wrapper-badge">
      <AdvancedEndDateBadge
        campaignStatus={campaignStatus}
        endTime={new Date(campaignEndTime)}
        getBadgeContent={getCampaignContent}
        currentTime={new Date()}
        formatDate={parseDateOnly}
      />
    </div>
  );

  const renderTitle = (size) => (
    <div className="wrapper-title">
      <Title size={size} title={templateData.campaignTitle} />
    </div>
  );

  const renderGoalProgressMeter = () => (
    <div className="wrapper-progress-meter">
      <GoalProgressMeter
        max={targetAmount}
        value={amountRaised}
        steps={amountRaisedWithCurrencySymbol}
        title={campaignGoalTitle}
      />
    </div>
  );

  const renderAvatarStack = () => (
    <div className="wrapper-avatar-stack">
      <WhosDonatedBanner
        numberOfContributors={campaignTotalNumberOfTxn}
        contributionsList={contributionsList.list}
        isLoading={isLoading}
      />
    </div>
  );

  const renderDonationName = () => (
    <div className="wrapper-donation-name">
      <BodyText>
        {donationName}
        {getPPCCStatus() === MERCHANT_PPCC_CONFIRMED_STATUS && <PPCC />}
      </BodyText>
    </div>
  );

  const headerClasses = classNames({
    'preview-header': true,
    sticky: stickToTop,
  });

  const renderHeader = () => (
    <div className="wrapper-header">
      {renderEndDateBadge()}
      {renderTitle('lg')}
      {renderAvatarStack()}
      {renderDonationName()}
      {renderGoalProgressMeter()}
    </div>
  );

  const renderDonorWall = () => {
    return (
      <>
        <LandingDonorWall
          contributionsList={contributionsList}
          isLoading={isLoading}
          currentPage={currentPage}
          handleShowMore={handleShowMore}
          totalNumberOfTransactions={campaignTotalNumberOfTxn}
        />
        {campaignStatus === 'ACTIVE' &&
        !isCampaignEnded(new Date(campaignEndTime)) &&
        contributionsList?.list?.length >= 10 ? (
          <Flyout contributionsList={contributionsList.list} />
        ) : null}
      </>
    );
  };

  const renderContributionsError = () => {
    return (
      <div style={{ textAlign: 'center' }}>
        <BodyText>{getCampaignContent('contributions.error.title')}</BodyText>
        <BodyText style={{ color: '#515354' }}>
          {getCampaignContent('contributions.error.body')}
        </BodyText>
        <Button
          tertiary
          onClick={() => {
            setRetry(retry + 1);
          }}
        >
          {getCampaignContent('contributions.error.cta')}
        </Button>
      </div>
    );
  };

  const renderContributions = () => {
    if (!contributionsError) {
      return renderDonorWall();
    } else if (currentPage === 1) {
      return renderContributionsError();
    } else {
      return (
        <>
          {renderDonorWall()}
          {renderContributionsError()}
        </>
      );
    }
  };

  const stickyHeader = stickToTop && (
    <div className={headerClasses}>
      <div className="wrapper-header">
        {renderEndDateBadge()}
        {renderTitle('sm')}
        {renderGoalProgressMeter()}
      </div>
    </div>
  );

  const personalizedMessages = {
    social_title: getSocialContent('subheader'),
  };

  const socialContent = () => {
    return (
      <div className="social-share-wrapper">
        <HeadingText
          size="sm"
          style={{ textAlign: `${!templateData.ismobile ? 'left' : 'center'}` }}
        >
          {messages.social.shareGeneric}
        </HeadingText>
        <ConfirmationSocialShare
          campaignId={templateData.productId}
          messages={messages}
          personalizedMessages={personalizedMessages}
          email={templateData.donationEmail}
          orgName={templateData.charityName}
          isLanding={true}
        />
      </div>
    );
  };

  const incrementDescription = (event) => {
    event.preventDefault();
    setDescriptionHeight((height) => {
      const incrementedHeight = height + MAX_HEIGHT_DESCRIPTION;
      if (incrementedHeight >= originalDescriptionHeight)
        setEnableReadMore(false);
      return incrementedHeight;
    });
  };

  const renderReadMore = () =>
    originalDescriptionHeight >= MAX_HEIGHT_DESCRIPTION &&
    enableReadMore && (
      <div className="campaign-read-more">
        <Button tertiary onClick={incrementDescription}>
          {getCampaignContent('readMore')}
        </Button>
      </div>
    );
  return (
    <div className="campaign-landing-container">
      <CampaignImage src={templateData.bannerUrl} />
      {!isMobile && stickyHeader}
      <div className="content-container">
        <Tile.Header />
        <div className="campaignDetails" data-testid="campaignDetails">
          <Tile.Content>
            <div className="campaign-title">{renderHeader()}</div>
            <div
              className="campaign-description"
              style={{
                maxHeight:
                  originalDescriptionHeight >= MAX_HEIGHT_DESCRIPTION
                    ? descriptionHeight + 'px'
                    : 'unset',
              }}
            >
              <div
                ref={(descriptionContainer) =>
                  descriptionContainer &&
                  setOriginalDescriptionHeight(
                    descriptionContainer.clientHeight
                  )
                }
              >
                <Suspense
                  fallback={
                    <LoadingSpinner size="md" style={{ margin: '0 auto' }} />
                  }
                >
                  <LandingRichTextDisplay
                    campaignDescription={templateData.campaignDescription}
                  />
                </Suspense>
              </div>
              {renderReadMore()}
            </div>
            <Suspense
              fallback={
                <LoadingSpinner size="md" style={{ margin: '0 auto' }} />
              }
            >
              {renderContributions()}
            </Suspense>
            {campaignTotalNumberOfTxn > 0 && socialContent()}
          </Tile.Content>
        </div>
        {!templateData.ismobile ? (
          <div className="paymentCard">
            <Landing />
          </div>
        ) : (
          <>
            <Col lg="12" className="mobileDonate">
              <Button
                onClick={() => {
                  setIsPaymentCardOpen(!isPaymentCardOpen);
                  trackDonateLandingButtonClick({ noteFieldDisplayed: null });
                }}
              >
                {getCampaignContent('donateButton')}
              </Button>
            </Col>
            <Sheet
              isOpen={isPaymentCardOpen}
              onClose={() => {}}
              title={getCampaignContent('donateButton')}
              mobileFixedHeight="90"
              className="mobilePreview darkBackground"
              wrapperClassName="mobilePreview-wrapper"
            >
              <Landing />
            </Sheet>
          </>
        )}
      </div>
    </div>
  );
};

export default withTheme(CampaignLanding);
